import React, { useEffect, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import AdminWordWheelService from "../tpl/AdminWordWheelService";
import {
    SEASON_LEVEL_CATALOG_MIN,
    WORD_WHEEL_DEFAULT_SEASON,
    calculatePuzzleLevel,
    comparePuzzleDifficulty,
    getDisplayPuzzleLevel,
    isLevelLocked,
    summarizeSeasonPuzzles,
} from "../wordWheelLevelUtils";

export default function WordWheelSeasonStatsDialog({
    open,
    onClose,
    language = "english",
    season = WORD_WHEEL_DEFAULT_SEASON,
    onLevelsAssigned,
}) {
    const [loading, setLoading] = useState(false);
    const [assigning, setAssigning] = useState(false);
    const [error, setError] = useState(null);
    const [assignResult, setAssignResult] = useState(null);
    const [puzzles, setPuzzles] = useState([]);

    useEffect(() => {
        if (!open) {
            return undefined;
        }
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setError(null);
            setAssignResult(null);
            try {
                const rows = await AdminWordWheelService.fetchPublishedBySeason(season, language);
                if (!cancelled) {
                    setPuzzles(rows);
                }
            } catch (err) {
                if (!cancelled) {
                    setError(err?.message || "Failed to load season puzzles.");
                    setPuzzles([]);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [open, season, language]);

    const summary = useMemo(() => summarizeSeasonPuzzles(puzzles), [puzzles]);

    const previewRows = useMemo(
        () => [...puzzles]
            .sort(comparePuzzleDifficulty)
            .slice(0, 20),
        [puzzles]
    );

    const handleAssignLevels = async () => {
        setAssigning(true);
        setError(null);
        setAssignResult(null);
        try {
            const result = await AdminWordWheelService.assignSeasonLevels(season, language);
            setAssignResult(result);
            const rows = await AdminWordWheelService.fetchPublishedBySeason(season, language);
            setPuzzles(rows);
            if (onLevelsAssigned && !result?.error) {
                onLevelsAssigned(result);
            }
        } catch (err) {
            setError(err?.message || "Failed to assign season levels.");
        } finally {
            setAssigning(false);
        }
    };

    return (
        <Dialog open={open} onClose={assigning ? undefined : onClose} maxWidth="md" fullWidth>
            <DialogTitle>Season stats + levels</DialogTitle>
            <DialogContent>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                        Published puzzles in {language} for {season}. Levels are assigned for the whole season once
                        you have at least {SEASON_LEVEL_CATALOG_MIN} puzzles.
                    </Typography>

                    {loading && (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                            <CircularProgress size={28} />
                        </Box>
                    )}

                    {error && <Alert severity="error">{error}</Alert>}

                    {assignResult && !assignResult.error && (
                        <Alert severity="success">
                            Assigned levels to {assignResult.updated ?? 0} puzzle(s).
                            {assignResult.levelLocked ? " Levels are now locked for this season." : ""}
                        </Alert>
                    )}

                    {!loading && (
                        <>
                            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 1 }}>
                                <StatBox label="Published" value={summary.total} />
                                <StatBox label="With level" value={summary.withLevel} />
                                <StatBox label="Without level" value={summary.withoutLevel} />
                                <StatBox label="Locked" value={summary.locked} />
                                <StatBox
                                    label="Level range"
                                    value={
                                        summary.levelMin != null && summary.levelMax != null
                                            ? `${summary.levelMin}–${summary.levelMax}`
                                            : "—"
                                    }
                                />
                            </Box>

                            {!summary.catalogComplete && (
                                <Alert severity="warning">
                                    {summary.total} of {SEASON_LEVEL_CATALOG_MIN} puzzles published. Add more puzzles
                                    before assigning season levels.
                                </Alert>
                            )}

                            {summary.catalogComplete && (
                                <Alert severity="info">
                                    Season catalog is ready. Assign levels from 1 (easiest: short words) to 1100
                                    (hardest): fewer characters, then fewer words, then higher total score.
                                </Alert>
                            )}

                            <TableContainer sx={{ maxHeight: 320 }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Title</TableCell>
                                            <TableCell align="right">Difficulty</TableCell>
                                            <TableCell align="right">Calculated</TableCell>
                                            <TableCell align="right">Level</TableCell>
                                            <TableCell align="center">Locked</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {previewRows.map((row) => (
                                            <TableRow key={row.id}>
                                                <TableCell>{row.title || row.name || "—"}</TableCell>
                                                <TableCell align="right">{row.difficultyLevel || "—"}</TableCell>
                                                <TableCell align="right">{calculatePuzzleLevel(row)}</TableCell>
                                                <TableCell align="right">{getDisplayPuzzleLevel(row) ?? "—"}</TableCell>
                                                <TableCell align="center">{isLevelLocked(row) ? "Yes" : "—"}</TableCell>
                                            </TableRow>
                                        ))}
                                        {previewRows.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={5} align="center">
                                                    No published puzzles in this season.
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {puzzles.length > previewRows.length && (
                                <Typography variant="caption" color="text.secondary">
                                    Showing first {previewRows.length} of {puzzles.length} puzzles (sorted by calculated difficulty).
                                </Typography>
                            )}
                        </>
                    )}
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} disabled={assigning}>
                    Close
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleAssignLevels}
                    disabled={loading || assigning || !summary.catalogComplete}
                    startIcon={assigning ? <CircularProgress size={16} color="inherit" /> : null}
                >
                    Calculate & assign levels
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function StatBox({ label, value }) {
    return (
        <Box sx={{ p: 1.5, border: 1, borderColor: "divider", borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary" display="block">
                {label}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {value}
            </Typography>
        </Box>
    );
}
